import { useMemo } from 'react';
import { NoBuilds } from '@bymason-private/controller-shared-widgets';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { AlertTitle, ControllerAlert, Divider, Text } from '@bymason-private/legacy-pattern-components';
import { useRouter } from '@bymason-private/route-provider';
import { JobModel } from '@bymason/controller-api-hooks';
import { BuildReview } from '../../../common/build-review.tsx';
import { routes } from '../../../router';

const useStyles = makeStyles(() => ({
  sectionSpacing: {
    marginBottom: 16,
    marginTop: 16,
  },
  heading: {
    marginBottom: 16,
  },
}));

export default function BuildsList({ projectId, projectBuilds, isBuildEnabled }: BuildsListProps) {
  const classes = useStyles();
  const { navigate } = useRouter();
  const configNumberFromHash = window.location.hash.slice(1);
  const sortedBuilds = useMemo(() => {
    return projectBuilds?.sort(({ createdAt: aDate }, { createdAt: bDate }) => {
      if (aDate < bDate) return 1;
      if (aDate > bDate) return -1;
      return 0;
    });
  }, [projectBuilds]);

  const latestBuild = useMemo(() => {
    return sortedBuilds?.[0];
  }, [sortedBuilds]);

  const prevBuilds = useMemo(() => sortedBuilds?.slice(1), [sortedBuilds]);

  const handleCreateBuild = () => {
    navigate(routes.ProjectBuildStepper, { projectId, stepId: 'apps' });
  };

  return (
    <>
      <section className={classes.sectionSpacing}>
        <div className={classes.heading}>
          <Divider />
          {latestBuild && <Text variant={ControllerFontType.Heading300B}>Current Build</Text>}
        </div>
        {latestBuild ? (
          <BuildReview
            projectName={latestBuild.project || ''}
            createdAt={latestBuild.createdAt}
            version={latestBuild.configVersion || ''}
            buildStatus={latestBuild.status}
            buildResult={latestBuild.result}
            defaultExpanded
          />
        ) : isBuildEnabled ? (
          <NoBuilds onBuildCreateClick={handleCreateBuild} />
        ) : (
          <ControllerAlert variant='standard' severity='info'>
            <AlertTitle>No current builds</AlertTitle>
          </ControllerAlert>
        )}
      </section>
      {latestBuild && (
        <section className={classes.sectionSpacing}>
          <div className={classes.heading}>
            <Divider />
            <Text variant={ControllerFontType.Heading300B}>Previous Builds</Text>
          </div>
          {prevBuilds && prevBuilds.length > 0 ? (
            prevBuilds.map((prevBuild) => {
              return (
                <BuildReview
                  key={prevBuild.configVersion}
                  projectName={prevBuild.project || ''}
                  createdAt={prevBuild.createdAt}
                  version={prevBuild.configVersion || ''}
                  buildStatus={prevBuild.status}
                  buildResult={prevBuild.result}
                  defaultExpanded={configNumberFromHash === prevBuild.configVersion}
                />
              );
            })
          ) : (
            <div>
              <ControllerAlert variant='standard' severity='info'>
                <AlertTitle>No previous builds</AlertTitle>
              </ControllerAlert>
            </div>
          )}
        </section>
      )}
    </>
  );
}

interface BuildsListProps {
  projectId: string;
  projectBuilds: JobModel[];
  isBuildEnabled?: boolean;
}
